// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Collection from "@kaspernj/api-maker/build/collection.js"
import SubjectRole from "./subject-role.js"

const modelClassData = {
  "attributes": {
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": true
    },
    "identifier": {
      "column": {
        "default": null,
        "name": "identifier",
        "null": false,
        "type": "string"
      },
      "name": "identifier",
      "selected_by_default": null,
      "translated": false
    },
    "name": {
      "column": null,
      "name": "name",
      "selected_by_default": null,
      "translated": true
    },
    "resource_type": {
      "column": {
        "default": null,
        "name": "resource_type",
        "null": false,
        "type": "string"
      },
      "name": "resource_type",
      "selected_by_default": null,
      "translated": false
    },
    "translated_resource_type": {
      "column": null,
      "name": "translated_resource_type",
      "selected_by_default": false,
      "translated": false
    }
  },
  "className": "Subject",
  "collectionKey": "subjects",
  "collectionName": "subjects",
  "i18nKey": "subject",
  "camelizedLower": "subject",
  "name": "Subject",
  "nameDasherized": "subject",
  "pluralName": "subjects",
  "ransackable_associations": [
    {
      "className": "BankAccount",
      "collectionName": "bank_accounts",
      "foreignKey": "subject_id",
      "name": "bank_accounts",
      "macro": "has_many",
      "resource_name": "BankAccount",
      "through": null
    },
    {
      "className": "Conversation",
      "collectionName": "conversations",
      "foreignKey": "subject_id",
      "name": "conversations",
      "macro": "has_many",
      "resource_name": "Conversation",
      "through": null
    },
    {
      "className": "Subject::Translation",
      "collectionName": null,
      "foreignKey": "subject_id",
      "name": "current_translation",
      "macro": "has_one",
      "resource_name": null,
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "Role",
      "collectionName": "roles",
      "foreignKey": "role_id",
      "name": "roles",
      "macro": "has_many",
      "resource_name": "Role",
      "through": "subject_roles"
    },
    {
      "className": "SubjectRole",
      "collectionName": "subject_roles",
      "foreignKey": "subject_id",
      "name": "subject_roles",
      "macro": "has_many",
      "resource_name": "SubjectRole",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "Subject::Translation",
      "collectionName": null,
      "foreignKey": "subject_id",
      "name": "translations",
      "macro": "has_many",
      "resource_name": null,
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "identifier",
      "column": {
        "default": null,
        "name": "identifier",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "old_id",
      "column": {
        "default": null,
        "name": "old_id",
        "null": true,
        "type": "integer"
      }
    },
    {
      "name": "resource_type",
      "column": {
        "default": null,
        "name": "resource_type",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "SubjectRole",
      "collectionName": "subject_roles",
      "foreignKey": "subject_id",
      "name": "subject_roles",
      "macro": "has_many",
      "resource_name": "SubjectRole",
      "through": null
    }
  ],
  "paramKey": "subject",
  "primaryKey": "id"
}

/** Frontend model for Subject. */
export default class Subject extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {string} */
  identifier() {
    return this.readAttributeUnderscore("identifier")
  }

  /** @returns {boolean} */
  hasIdentifier() {
    const value = this.identifier()

    return this._isPresent(value)
  }

  /** @returns {string | null} */
  name() {
    return this.readAttributeUnderscore("name")
  }

  /** @returns {boolean} */
  hasName() {
    const value = this.name()

    return this._isPresent(value)
  }

  /** @returns {string} */
  resourceType() {
    return this.readAttributeUnderscore("resource_type")
  }

  /** @returns {boolean} */
  hasResourceType() {
    const value = this.resourceType()

    return this._isPresent(value)
  }

  /** @returns {any} */
  translatedResourceType() {
    return this.readAttributeUnderscore("translated_resource_type")
  }

  /** @returns {boolean} */
  hasTranslatedResourceType() {
    const value = this.translatedResourceType()

    return this._isPresent(value)
  }

  /** @returns {import("@kaspernj/api-maker/build/collection.js").default<typeof import("./subject-role.js").default>} */
  subjectRoles() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["subject_id_eq"] = this.id()

    return new Collection(
      {
        reflectionName: "subject_roles",
        model: this,
        modelClass: SubjectRole
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./subject-role.js").default>>} */
  loadSubjectRoles() {
    if (!("id" in this)) throw new Error("No such primary key method: id")

    const ransack = {}

    ransack["subject_id_eq"] = this.id()

    return this._loadHasManyReflection(
      {
        reflectionName: "subject_roles",
        model: this,
        modelClass: SubjectRole
      },
      {ransack}
    )
  }

  /** @returns {Promise<Array<import("./subject-role.js").default>>} */
  ensureSubjectRolesLoaded() {
    return this.ensureAssociationLoaded("subject_roles")
  }
}
